import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { outlineToSections } from '../utils/courseOutlineApply';
import './AssignmentAssistant.css';
import AssistantInfoBubble from './AssistantInfoBubble';

const LOADING_STEPS = [
  'Planning modules…',
  'Sequencing lessons…',
  'Drafting content items…',
];

const FRESH_REQUEST_NOTE =
  'NOTICE: Only the text you enter above is sent to the AI. No previous prompts, course data, or chat history is included. This is a LOW TIER AI ASSISTANT for cost & demo integration purposes only.';

const ITEM_TYPE_LABEL = {
  Content: 'Content',
  Assignment: 'Assignment',
  'Discussion Board': 'Discussion',
};

const CourseAssistant = ({
  courseTitle,
  setCourseTitle,
  courseTrack,
  gradeLevels,
  defaultPrimaryConcentration,
  currentCourseId,
  sections,
  setSections,
  aiCapabilities = null,
  onOutlineInserted,
}) => {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [activePrompt, setActivePrompt] = useState(null);
  const [input, setInput] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [applying, setApplying] = useState(false);
  const [loadOptions, setLoadOptions] = useState({
    outline: true,
    useTitle: true,
    append: false,
    replace: false,
  });

  const capabilitiesLoaded = aiCapabilities != null;
  const canUseAssistant = aiCapabilities?.UseAssistant === true;

  const isLoading = activePrompt?.status === 'loading';
  const nextPromptNumber = promptHistory.length + 1;
  const displayPromptNumber = activePrompt?.number ?? nextPromptNumber;

  const pendingOutline = activePrompt?.result;

  const outlinePreview = useMemo(() => {
    if (!pendingOutline?.modules) return [];
    return pendingOutline.modules.map((mod) => ({
      title: mod.title,
      lessons: mod.lessons.map((lesson) => ({
        title: lesson.title,
        items: lesson.items.map((i) => `${ITEM_TYPE_LABEL[i.type] || i.type}: ${i.title}`),
      })),
    }));
  }, [pendingOutline]);

  useEffect(() => {
    if (!pendingOutline?.modules?.length || activePrompt?.status !== 'done') return;

    const hasExisting = (sections?.length || 0) > 0;
    setLoadOptions({
      outline: true,
      useTitle: Boolean(pendingOutline.course_title),
      append: hasExisting,
      replace: false,
    });
  }, [activePrompt?.id, activePrompt?.status, pendingOutline, sections?.length]);

  useEffect(() => {
    if (!isLoading) return undefined;
    setLoadingStep(0);
    const interval = setInterval(() => {
      setLoadingStep((s) => (s + 1) % LOADING_STEPS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const buildApiContext = useCallback(
    () => ({
      fresh_request: true,
      course_track: courseTrack || 'CTE',
      grade_levels: gradeLevels || [],
      max_modules: 4,
    }),
    [courseTrack, gradeLevels]
  );

  const callOutlineApi = useCallback(
    async (promptText) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Please log in to use the course assistant.');

      const response = await axios.post(
        '/api/ai/course-outline',
        {
          messages: [{ role: 'user', content: promptText.trim() }],
          context: buildApiContext(),
          options: { max_modules: 4 },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    },
    [buildApiContext]
  );

  const completePrompt = useCallback(
    (actionLabel) => {
      if (!activePrompt) return;
      setPromptHistory((prev) => [
        ...prev,
        {
          id: activePrompt.id,
          number: activePrompt.number,
          text: activePrompt.text,
          action: actionLabel,
          stats: activePrompt.result?.stats || null,
        },
      ]);
      setActivePrompt(null);
      setInput('');
      setShowHistory(false);
    },
    [activePrompt]
  );

  const generatePrompt = useCallback(
    async (promptText, number, existingId = null) => {
      const id = existingId || uuidv4();
      setError('');
      setShowHistory(false);
      setActivePrompt({
        id,
        number,
        text: promptText,
        status: 'loading',
        error: '',
        result: null,
      });

      try {
        const data = await callOutlineApi(promptText);
        setActivePrompt((prev) =>
          prev && prev.id === id
            ? { ...prev, status: 'done', result: data, error: '' }
            : prev
        );
      } catch (err) {
        const msg =
          [err.response?.data?.error, err.response?.data?.detail].filter(Boolean).join(': ') ||
          err.message ||
          'Failed to generate outline.';
        setError(msg);
        setActivePrompt((prev) =>
          prev && prev.id === id
            ? { ...prev, status: 'error', error: msg, result: null }
            : prev
        );
      }
    },
    [callOutlineApi]
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || activePrompt?.status === 'done') return;
    setInput('');
    await generatePrompt(text, nextPromptNumber);
  }, [input, isLoading, activePrompt, nextPromptNumber, generatePrompt]);

  const handleRegenerate = useCallback(async () => {
    if (!activePrompt?.text || isLoading) return;
    await generatePrompt(activePrompt.text, activePrompt.number, activePrompt.id);
  }, [activePrompt, isLoading, generatePrompt]);

  const handleDiscard = useCallback(() => {
    setActivePrompt(null);
    setInput('');
    setError('');
  }, []);

  const handleLoad = useCallback(async () => {
    const outline = activePrompt?.result;
    if (!outline?.modules?.length) return;

    const { outline: loadOutline, useTitle, append, replace } = loadOptions;

    if (!loadOutline && !useTitle) return;

    setApplying(true);
    setError('');

    try {
      let mergedSections = sections || [];
      const hasExisting = (sections?.length || 0) > 0;
      const doReplace = hasExisting && replace;
      const doAppend = hasExisting && append && !replace;

      if (loadOutline) {
        if (hasExisting && !append && !replace) {
          throw new Error('Choose append or replace when loading into an existing outline.');
        }

        const newSections = outlineToSections(outline, {
          courseId: currentCourseId,
          defaultPrimaryConcentration,
          startIndex: doReplace ? 0 : sections?.length || 0,
        });

        mergedSections = doReplace
          ? newSections
          : hasExisting
            ? [...(sections || []), ...newSections]
            : newSections;
        setSections(mergedSections);
      }

      if (useTitle && outline.course_title) {
        setCourseTitle(outline.course_title);
      }

      const parts = [];
      if (loadOutline) parts.push(doReplace ? 'replace outline' : doAppend ? 'append outline' : 'outline');
      if (useTitle && outline.course_title) parts.push('title');

      completePrompt(parts.length ? `Load (${parts.join(', ')})` : 'Load');
      setToast('Outline loaded — review components and Save when ready.');

      if (loadOutline && onOutlineInserted) {
        onOutlineInserted(mergedSections);
      }
    } catch (err) {
      setError(err.message || 'Failed to load outline.');
    } finally {
      setApplying(false);
    }
  }, [
    activePrompt,
    loadOptions,
    sections,
    currentCourseId,
    defaultPrimaryConcentration,
    setSections,
    setCourseTitle,
    completePrompt,
    onOutlineInserted,
  ]);

  const renderActiveResult = () => {
    if (!pendingOutline?.modules?.length) return null;

    const hasExisting = (sections?.length || 0) > 0;
    const canLoadOutline =
      loadOptions.outline &&
      pendingOutline.modules.length > 0 &&
      (!hasExisting || loadOptions.append || loadOptions.replace);
    const canLoad =
      canLoadOutline || (loadOptions.useTitle && pendingOutline.course_title);

    return (
      <div className="aa-review">
        <h4>Outline review</h4>
        {pendingOutline.reasoning_summary && (
          <p className="aa-review__summary">{pendingOutline.reasoning_summary}</p>
        )}
        <p>
          <strong>{pendingOutline.course_title}</strong>
          {' · '}
          {pendingOutline.stats?.module_count || 0} modules
          {' · '}
          {pendingOutline.stats?.lesson_count || 0} lessons
          {' · '}
          {pendingOutline.stats?.item_count || 0} items
        </p>
        {pendingOutline.course_description && (
          <p>{pendingOutline.course_description}</p>
        )}
        <ul>
          {outlinePreview.slice(0, 4).map((mod, mi) => (
            <li key={mi}>
              <strong>{mod.title}</strong>
              <ul>
                {mod.lessons.slice(0, 3).map((lesson, li) => (
                  <li key={li}>
                    {lesson.title}
                    {lesson.items.length > 0 && (
                      <span style={{ color: '#64748b', fontSize: '12px' }}>
                        {' '}
                        — {lesson.items.slice(0, 3).join('; ')}
                        {lesson.items.length > 3 ? '…' : ''}
                      </span>
                    )}
                  </li>
                ))}
                {mod.lessons.length > 3 && (
                  <li>…and {mod.lessons.length - 3} more lessons</li>
                )}
              </ul>
            </li>
          ))}
          {outlinePreview.length > 4 && (
            <li>…and {outlinePreview.length - 4} more modules</li>
          )}
        </ul>

        <div className="aa-load">
          <div className="aa-load__options">
            <label className="aa-checkbox">
              <input
                type="checkbox"
                checked={loadOptions.outline}
                onChange={(e) =>
                  setLoadOptions((o) => ({ ...o, outline: e.target.checked }))
                }
                disabled={applying}
              />
              Outline (modules, lessons, items)
            </label>
            {pendingOutline.course_title && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.useTitle}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, useTitle: e.target.checked }))
                  }
                  disabled={applying}
                />
                Use suggested course title
              </label>
            )}
            {hasExisting && (
              <>
                <label className="aa-checkbox">
                  <input
                    type="checkbox"
                    checked={loadOptions.append}
                    onChange={(e) => {
                      setLoadOptions((o) => ({
                        ...o,
                        append: e.target.checked,
                        replace: e.target.checked ? false : o.replace,
                      }));
                    }}
                    disabled={applying}
                  />
                  Append modules to existing outline
                </label>
                <label className="aa-checkbox">
                  <input
                    type="checkbox"
                    checked={loadOptions.replace}
                    onChange={(e) => {
                      setLoadOptions((o) => ({
                        ...o,
                        replace: e.target.checked,
                        append: e.target.checked ? false : o.append,
                      }));
                    }}
                    disabled={applying}
                  />
                  Replace existing outline
                </label>
              </>
            )}
          </div>
          <div className="aa-load__actions">
            <button
              type="button"
              className="aa-btn aa-btn--primary"
              onClick={handleLoad}
              disabled={applying || isLoading || !canLoad}
            >
              {applying ? 'Loading…' : 'Load'}
            </button>
            <button
              type="button"
              className="aa-icon-btn"
              onClick={handleRegenerate}
              disabled={applying || isLoading}
              aria-label="Regenerate"
              title="Regenerate"
            >
              ⟲
            </button>
            <button
              type="button"
              className="aa-btn aa-btn--ghost aa-btn--sm"
              onClick={handleDiscard}
              disabled={applying}
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!capabilitiesLoaded || !canUseAssistant) {
    return null;
  }

  const inCompose =
    !showHistory && (!activePrompt || activePrompt.status === 'error');
  const inResult =
    !showHistory && activePrompt && activePrompt.status !== 'error';

  return (
    <>
      {open && (
        <div className="aa-panel" role="dialog" aria-label="AI prompt">
          <div className="aa-panel__body">
            {showHistory ? (
              <div className="aa-prompt aa-prompt--active">
                <div className="aa-prompt__header">
                  <span className="aa-prompt__label">Previous prompts</span>
                  <div className="aa-prompt__header-actions">
                    <AssistantInfoBubble>
                      <p>Generate a course outline from your prompt, then load:</p>
                      <ul>
                        <li>Modules, lessons, and content items</li>
                        <li>Suggested course title</li>
                        <li>Append or replace an existing outline</li>
                      </ul>
                      <span className="aa-info-bubble__badge">In development</span>
                      <p className="aa-info-bubble__note">
                        Cannot create assignments yet — assignment placeholders in outlines are not filled in.
                      </p>
                    </AssistantInfoBubble>
                    <button
                      type="button"
                      className="aa-icon-btn"
                      onClick={() => setShowHistory(false)}
                      aria-label="Back to current prompt"
                      title="Back"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      className="aa-icon-btn aa-icon-btn--close"
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="aa-history">
                  <p className="aa-history__intro">
                    This session only — cleared when you refresh or leave the page.
                  </p>
                  {promptHistory.map((entry) => (
                    <div key={entry.id} className="aa-history__item">
                      <div className="aa-history__head">
                        <span className="aa-prompt__label">Prompt #{entry.number}</span>
                        <span className="aa-history__action">{entry.action}</span>
                      </div>
                      <p className="aa-history__text">{entry.text}</p>
                      {entry.stats && (
                        <p className="aa-history__meta">
                          {entry.stats.module_count || 0} modules ·{' '}
                          {entry.stats.item_count || 0} items
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="aa-prompt aa-prompt--active">
                <div className="aa-prompt__header">
                  <span className="aa-prompt__label">Prompt #{displayPromptNumber}</span>
                  <div className="aa-prompt__header-actions">
                    <AssistantInfoBubble>
                      <p>Generate a course outline from your prompt, then load:</p>
                      <ul>
                        <li>Modules, lessons, and content items</li>
                        <li>Suggested course title</li>
                        <li>Append or replace an existing outline</li>
                      </ul>
                      <span className="aa-info-bubble__badge">In development</span>
                      <p className="aa-info-bubble__note">
                        Cannot create assignments yet — assignment placeholders in outlines are not filled in.
                      </p>
                    </AssistantInfoBubble>
                    {promptHistory.length > 0 && (
                      <button
                        type="button"
                        className="aa-icon-btn"
                        onClick={() => setShowHistory(true)}
                        aria-label={`Previous prompts (${promptHistory.length})`}
                        title={`Previous prompts (${promptHistory.length})`}
                      >
                        <span className="aa-icon-btn__glyph" aria-hidden>
                          ⟲
                        </span>
                        <span className="aa-icon-btn__badge">{promptHistory.length}</span>
                      </button>
                    )}
                    {activePrompt?.status === 'loading' && (
                      <span className="aa-prompt__status aa-prompt__status--loading">
                        Generating…
                      </span>
                    )}
                    <button
                      type="button"
                      className="aa-icon-btn aa-icon-btn--close"
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {inCompose && (
                  <>
                    <textarea
                      id="ca-prompt-input"
                      className="aa-prompt__input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Describe the course topic, grade level, length, and what students should learn…"
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    <p className="aa-fresh-note">{FRESH_REQUEST_NOTE}</p>
                    {activePrompt?.status !== 'error' && (
                      <div className="aa-prompt__generate">
                        <button
                          type="button"
                          className="aa-btn aa-btn--primary aa-btn--block"
                          onClick={handleSend}
                          disabled={isLoading || !input.trim()}
                        >
                          {isLoading ? 'Working…' : 'Generate'}
                        </button>
                      </div>
                    )}
                    {activePrompt?.status === 'error' && (
                      <>
                        <div className="aa-prompt__text">{activePrompt.text}</div>
                        <div className="aa-prompt__error">{activePrompt.error}</div>
                        <div className="aa-prompt__actions">
                          <button
                            type="button"
                            className="aa-btn aa-btn--secondary aa-btn--block"
                            onClick={handleRegenerate}
                            disabled={isLoading}
                          >
                            Try again
                          </button>
                          <button
                            type="button"
                            className="aa-btn aa-btn--ghost aa-btn--block"
                            onClick={handleDiscard}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {inResult && (
                  <>
                    <div className="aa-prompt__text">{activePrompt.text}</div>
                    {activePrompt.status === 'loading' && (
                      <div className="aa-loading" role="status">
                        <span className="aa-loading__spinner" aria-hidden />
                        {LOADING_STEPS[loadingStep]}
                      </div>
                    )}
                    {activePrompt.status === 'done' && renderActiveResult()}
                  </>
                )}
              </div>
            )}

            {error && !isLoading && (
              <div
                className="aa-toast"
                style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}
              >
                {error}
              </div>
            )}

            {toast && <div className="aa-toast">{toast}</div>}
          </div>
        </div>
      )}

      <div className="aa-root">
        <button
          type="button"
          className={`aa-bubble${open ? ' aa-bubble--open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Close AI' : 'Open AI'}
          title="AI"
        >
          ✦
        </button>
      </div>
    </>
  );
};

export default CourseAssistant;
